"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { hasCSVData } from "@/lib/csv/csv-storage";
import { loadSampleData } from "@/lib/csv/load-sample";
import DarkModeToggle from "@/components/csv/DarkModeToggle";

export default function LandingPage() {
  const router = useRouter();
  const [hasData, setHasData] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    hasCSVData().then((has) => {
      setHasData(has);
      setChecked(true);
    });
  }, []);

  async function handleAutoLoad() {
    setAutoLoading(true);
    try {
      await loadSampleData();
      router.push("/csv/dashboard");
    } catch {
      setAutoLoading(false);
    }
  }

  if (!checked) return null;

  return (
    <div className="flex flex-col items-center gap-8 px-6 py-12">
      <div className="absolute top-4 right-4">
        <DarkModeToggle />
      </div>

      <div className="flex items-center gap-2">
        <Image src="/icons/logo.svg" width={40} height={40} alt="Mel's Budget" />
        <h1 className="text-30 font-bold text-gray-900 dark:text-gray-100">
          Mel&apos;s Budget
        </h1>
      </div>

      <p className="max-w-md text-center text-16 text-gray-600 dark:text-gray-400">
        Track your finances, view transactions, and manage your money — all in
        one place.
      </p>

      {/* If data exists, go straight to dashboard */}
      {hasData && (
        <Link
          href="/csv/dashboard"
          className="w-full max-w-md rounded-xl bg-bank-gradient px-8 py-4 text-center text-16 font-semibold text-white shadow-form transition-all hover:opacity-90"
        >
          Open Dashboard &rarr;
        </Link>
      )}

      <div className="grid w-full max-w-2xl gap-6 sm:grid-cols-2">
        {/* Connect Bank - coming soon */}
        <div
          className="relative flex flex-col gap-4 rounded-2xl border border-gray-200 dark:border-[#2d3142] bg-white dark:bg-[#1a1d28] p-8 shadow-sm opacity-75 cursor-default"
        >
          <span className="absolute top-3 right-3 rounded-full bg-blue-100 dark:bg-blue-900/40 px-2.5 py-0.5 text-10 font-semibold text-blue-700 dark:text-blue-300">
            Coming Soon
          </span>
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30">
            <svg className="h-7 w-7 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <div>
            <h2 className="text-20 font-semibold text-gray-900 dark:text-gray-100">Connect Bank</h2>
            <p className="mt-2 text-14 text-gray-500 dark:text-gray-400">Link your bank account for real-time balances, automatic transaction sync, and fund transfers via Plaid &amp; Dwolla.</p>
          </div>
          <span className="mt-auto text-14 font-medium text-gray-400 dark:text-gray-500">Coming soon &rarr;</span>
        </div>

        {/* Upload CSV */}
        <Link
          href="/csv"
          className="group flex flex-col gap-4 rounded-2xl border border-gray-200 dark:border-[#2d3142] bg-white dark:bg-[#1a1d28] p-8 shadow-sm transition-all hover:border-green-300 hover:shadow-lg"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-green-100 dark:bg-green-900/30">
            <svg className="h-7 w-7 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <div>
            <h2 className="text-20 font-semibold text-gray-900 dark:text-gray-100">Upload CSV</h2>
            <p className="mt-2 text-14 text-gray-500 dark:text-gray-400">Import bank statements from any bank. Works entirely in your browser.</p>
          </div>
          <span className="mt-auto text-14 font-medium text-green-600 dark:text-green-400">Upload &rarr;</span>
        </Link>

        {/* Quick load sample data */}
        {!hasData && (
          <button
            onClick={handleAutoLoad}
            disabled={autoLoading}
            className="group flex flex-col gap-4 rounded-2xl border border-gray-200 dark:border-[#2d3142] bg-white dark:bg-[#1a1d28] p-8 shadow-sm transition-all hover:border-blue-300 hover:shadow-lg text-left disabled:opacity-50"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30">
              <svg className="h-7 w-7 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </div>
            <div>
              <h2 className="text-20 font-semibold text-gray-900 dark:text-gray-100">
                {autoLoading ? "Loading..." : "Load Mel's Budget 2026"}
              </h2>
              <p className="mt-2 text-14 text-gray-500 dark:text-gray-400">Pre-loaded budget with 5 pay periods, 33 transactions. See your Out of Debt plan.</p>
            </div>
            <span className="mt-auto text-14 font-medium text-blue-600 dark:text-blue-400">
              {autoLoading ? "Setting up..." : "Load & go →"}
            </span>
          </button>
        )}
      </div>

      {/* Auth + Download template */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex gap-3">
          <Link
            href="/sign-in"
            className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-14 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Sign In
          </Link>
          <Link
            href="/sign-up"
            className="rounded-lg bg-bank-gradient px-4 py-2 text-14 font-medium text-white hover:opacity-90 transition-opacity"
          >
            Create Account
          </Link>
        </div>
        <p className="text-12 text-gray-400 dark:text-gray-500">
          Sign in to save data in the cloud. Or use CSV mode without an account.
        </p>
        <a
          href="/sample-data/upload-template.csv"
          download="budget-template.csv"
          className="text-14 text-gray-500 dark:text-gray-400 underline hover:text-gray-700 dark:hover:text-gray-200"
        >
          Download CSV template
        </a>
      </div>
    </div>
  );
}
